import Agilite from 'agilite'
import store from '../../../../store'
import BillingEnums from '../../utils/enums'
import { createLedgerTransaction, updateFollowUpAccountById, getFollowUpAccounts } from './index'
import { logAuditEvent, AuditActions, AuditEntityTypes } from './auditTrail'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

/**
 * Get credit notes
 * @param {Object} filter - MongoDB filter
 * @param {number} page - Page number
 * @param {number} pageLimit - Items per page
 * @returns {Promise} Credit notes
 */
export const getCreditNotes = (filter = {}, page = 1, pageLimit = 50) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.CREDIT_NOTES,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify(filter),
            page,
            pageLimit
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get credit note by ID
 * @param {string} creditNoteId - Credit note ID
 * @returns {Promise} Credit note details
 */
export const getCreditNoteById = (creditNoteId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.CREDIT_NOTES,
          BillingEnums.routeKeys.FIND_BY_ID,
          {
            recordId: creditNoteId
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get invoice details by ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise} Invoice details
 */
export const getInvoiceDetails = (invoiceId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Get invoice from invoices collection
        const response = await agilite.Connectors.execute('invoices', BillingEnums.routeKeys.FIND_BY_ID, {
          recordId: invoiceId
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Create credit note
 * @param {Object} data - Credit note data
 * @returns {Promise} Created credit note
 */
export const createCreditNote = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          entityRef: store.getState().core.entity._id,
          createdBy: store.getState().auth.agiliteUser?._id,
          createdAt: new Date(),
          creditNoteNo: data.creditNoteNo || `CN-${Date.now()}`,
          status: 'active',
          remainingAmount: data.amount,
          appliedAmount: 0
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.CREDIT_NOTES,
          BillingEnums.routeKeys.CREATE,
          {
            data: JSON.stringify(data)
          }
        )

        // Log audit trail for credit note creation
        await logAuditEvent(
          AuditActions.CREATE,
          AuditEntityTypes.CREDIT_NOTE,
          response.data._id,
          `Credit note ${data.creditNoteNo} created for R${data.amount}`,
          {
            creditNoteNo: data.creditNoteNo,
            amount: data.amount,
            reason: data.reason,
            patientId: data.patientId,
            invoiceNo: data.invoiceNo,
            billingId: data.billingId
          }
        )

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update credit note
 * @param {string} creditNoteId - Credit note ID
 * @param {Object} data - Update data
 * @returns {Promise} Updated credit note
 */
export const updateCreditNote = (creditNoteId, data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          modifiedBy: store.getState().auth.agiliteUser?._id,
          modifiedAt: new Date()
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.CREDIT_NOTES,
          BillingEnums.routeKeys.UPDATE,
          {
            id: creditNoteId,
            data: JSON.stringify(data)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Apply credit note to invoice
 * @param {Object} applicationData - Application details
 * @returns {Promise} Application result
 */
export const applyCreditNote = (applicationData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const { creditNoteId, invoiceId, amount } = applicationData

        // Get credit note
        const creditNote = await getCreditNoteById(creditNoteId)
        if (!creditNote) {
          throw new Error('Credit note not found')
        }

        // Validate amount
        if (parseFloat(amount) > parseFloat(creditNote.remainingAmount)) {
          throw new Error('Application amount exceeds remaining credit')
        }

        // Update credit note
        await updateCreditNote(creditNoteId, {
          appliedAmount: parseFloat(creditNote.appliedAmount) + parseFloat(amount),
          remainingAmount: parseFloat(creditNote.remainingAmount) - parseFloat(amount),
          status:
            parseFloat(creditNote.remainingAmount) - parseFloat(amount) <= 0 ? 'fully_applied' : 'partially_applied'
        })

        // Create ledger transaction
        await createLedgerTransaction({
          patientId: creditNote.patientId,
          type: BillingEnums.transactionTypes.CREDIT_NOTE,
          description: `Credit Note ${creditNote.creditNoteNo} applied to Invoice ${invoiceId}`,
          debit: 0,
          credit: amount,
          creditNoteRef: creditNoteId,
          invoiceRef: invoiceId,
          status: 'Applied'
        })

        resolve({ success: true, appliedAmount: amount })
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Process a refund against a credit note
 * @param {Object} refundData - Refund details
 * @returns {Promise} Processed refund
 */
export const processCreditRefund = (refundData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Validate credit note exists
        const creditNote = await getCreditNoteById(refundData.creditNoteId)
        if (!creditNote) {
          throw new Error('Credit note not found')
        }

        // Check if refund amount is valid
        if (parseFloat(refundData.amount) > parseFloat(creditNote.remainingAmount)) {
          throw new Error('Refund amount exceeds remaining credit')
        }

        // Update credit note
        const updatedCreditNote = await updateCreditNote(refundData.creditNoteId, {
          appliedAmount: parseFloat(creditNote.appliedAmount) + parseFloat(refundData.amount),
          remainingAmount: parseFloat(creditNote.remainingAmount) - parseFloat(refundData.amount),
          status:
            parseFloat(creditNote.remainingAmount) - parseFloat(refundData.amount) <= 0
              ? 'fully_applied'
              : 'partially_applied'
        })

        resolve(updatedCreditNote)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Generate unique credit note number
 * @returns {Promise<string>} Credit note number
 */
const generateCreditNoteNumber = async () => {
  const year = new Date().getFullYear()
  const month = String(new Date().getMonth() + 1).padStart(2, '0')

  // Get the latest credit note number
  const filter = {
    entityRef: store.getState().core.entity._id,
    creditNoteNo: { $regex: `^CN${year}${month}` }
  }

  const latestCreditNotes = await agilite.Connectors.execute(
    BillingEnums.profileKeys.CREDIT_NOTES,
    BillingEnums.routeKeys.READ,
    {
      filter: JSON.stringify(filter),
      sort: JSON.stringify({ creditNoteNo: -1 }),
      pageLimit: 1
    }
  )

  let sequence = 1
  if (latestCreditNotes.data && latestCreditNotes.data.length > 0) {
    const lastNumber = latestCreditNotes.data[0].creditNoteNo
    const lastSequence = parseInt(lastNumber.substring(8))
    sequence = lastSequence + 1
  }

  return `CN${year}${month}${String(sequence).padStart(4, '0')}`
}
